// Local image assets for WG listings
import wgRoom1 from '../assets/images/wg-room-1.jpg';
import wgRoom2 from '../assets/images/wg-room-2.jpg';
import wgRoom3 from '../assets/images/wg-room-3.jpg';
import wgRoom4 from '../assets/images/wg-room-4.jpg';
import wgRoom5 from '../assets/images/wg-room-5.jpg';
import wgRoom6 from '../assets/images/wg-room-6.jpg';
import wgRoom7 from '../assets/images/wg-room-7.jpg';
import wgRoom8 from '../assets/images/wg-room-8.jpg';
import type { Listing } from '../types';
import { API_BASE_URL, ASSETS_BASE_URL } from './config';

const ABSOLUTE_URL_PATTERN = /^(?:https?:|data:|blob:)/i;
const PROTOCOL_RELATIVE_PATTERN = /^\/\//;
const STORAGE_SCHEME_PATTERN = /^(?:minio|s3):\/\//i;

const joinUrl = (base: string, path: string): string => {
  const normalizedBase = base.replace(/\/+$/, '');
  const normalizedPath = path.replace(/^\/+/, '');
  return `${normalizedBase}/${normalizedPath}`;
};

const normalizeImageUrl = (value?: string | null): string | undefined => {
  if (!value) {
    return undefined;
  }

  const trimmed = value.trim();
  if (!trimmed) {
    return undefined;
  }

  if (ABSOLUTE_URL_PATTERN.test(trimmed)) {
    return trimmed;
  }

  if (PROTOCOL_RELATIVE_PATTERN.test(trimmed)) {
    return `https:${trimmed}`;
  }

  const withoutStorageScheme = trimmed.replace(STORAGE_SCHEME_PATTERN, '');
  const cleanedPath = withoutStorageScheme.replace(/^\/+/, '');

  if (ASSETS_BASE_URL) {
    return joinUrl(ASSETS_BASE_URL, cleanedPath);
  }

  if (trimmed.startsWith('/') && typeof window !== 'undefined' && window.location?.origin) {
    return joinUrl(window.location.origin, cleanedPath);
  }

  if (API_BASE_URL && API_BASE_URL !== '/api') {
    return joinUrl(API_BASE_URL, cleanedPath);
  }

  return `/${cleanedPath}`;
};

// Curated list of CC0/Unsplash test images for WG rooms.
// The Unsplash URLs include the `ixid` attribution parameter as required by the licence terms.
const openSourceRoomImages: string[] = [
  'https://images.unsplash.com/photo-1505693416388-ac5ce068fe85?auto=format&fit=crop&w=1200&q=80&ixlib=rb-4.0.3&ixid=MnwyMDc0MzR8MHwxfHNlYXJjaHwyfHxhcGFydG1lbnR8ZW58MHx8fHwxNzA5MDU3NjQy',
  'https://images.unsplash.com/photo-1501183638710-841dd1904471?auto=format&fit=crop&w=1200&q=80&ixlib=rb-4.0.3&ixid=MnwyMDc0MzR8MHwxfHNlYXJjaHwxfHxzdHVkaW98ZW58MHx8fHwxNzA5MDU3NjUy',
  'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?auto=format&fit=crop&w=1200&q=80&ixlib=rb-4.0.3&ixid=MnwyMDc0MzR8MHwxfHNlYXJjaHw3fHxiZWRyb29tfGVufDB8fHx8MTcwOTA1NzY2Nw',
  'https://images.unsplash.com/photo-1484100356142-db6ab6244067?auto=format&fit=crop&w=1200&q=80&ixlib=rb-4.0.3&ixid=MnwyMDc0MzR8MHwxfHNlYXJjaHwxfHxzdHVkaW8lMjBkZXNrfGVufDB8fHx8MTcwOTA1NzY3NQ'
];

// Array of local WG room images with additional remote fallbacks
export const wgRoomImages: string[] = [
  wgRoom1,
  wgRoom2,
  wgRoom3,
  wgRoom4,
  wgRoom5,
  wgRoom6,
  wgRoom7,
  wgRoom8,
  ...openSourceRoomImages
];

/**
 * Get a random WG room image
 * @returns A random image URL from the local assets
 */
export const getRandomWgImage = (): string => {
  const randomIndex = Math.floor(Math.random() * wgRoomImages.length);
  return wgRoomImages[randomIndex];
};

/**
 * Get a specific WG room image by index (for consistent assignment)
 * @param index The index of the image to return
 * @returns The image URL at the specified index
 */
export const getWgImageByIndex = (index: number): string => {
  const length = wgRoomImages.length;
  if (length === 0) {
    throw new Error('No WG room images configured');
  }

  const safeIndex = ((index % length) + length) % length;
  return wgRoomImages[safeIndex];
};

/**
 * Get a WG room image by ID (for consistent assignment based on listing ID)
 * @param id The listing ID
 * @returns A consistent image URL based on the ID
 */
export const getWgImageById = (id: number): string => {
  const normalizedId = Number.isFinite(id) && id > 0 ? Math.floor(id) : 1;
  return getWgImageByIndex(normalizedId - 1);
};

/**
 * Determine the best image URL for a given listing.
 *
 * Priority:
 * 1. Explicit imageUrl provided by the backend/user
 * 2. Legacy `image` property from the backend
 * 3. Deterministic fallback based on the listing id
 * 4. Fallback based on the provided index (e.g. array order)
 * 5. Random local/remote placeholder
 */
export const getListingImage = (
  listing: Listing | null | undefined,
  fallbackIndex?: number
): string | undefined => {
  if (!listing) {
    return undefined;
  }

  const resolvedExplicitImage = normalizeImageUrl(listing.imageUrl);
  if (resolvedExplicitImage) {
    return resolvedExplicitImage;
  }

  const resolvedLegacyImage = normalizeImageUrl(listing.image);
  if (resolvedLegacyImage) {
    return resolvedLegacyImage;
  }

  if (typeof listing.id === 'number' && Number.isFinite(listing.id) && listing.id > 0) {
    return getWgImageById(listing.id);
  }

  if (typeof fallbackIndex === 'number') {
    return getWgImageByIndex(fallbackIndex);
  }

  return getRandomWgImage();
};

/**
 * Return a copy of the listing with a resolved `imageUrl` property.
 */
export const withListingImage = <T extends Listing>(listing: T, fallbackIndex?: number): T => {
  const resolvedImage = getListingImage(listing, fallbackIndex);
  if (!resolvedImage || listing.imageUrl === resolvedImage) {
    return listing;
  }

  return {
    ...listing,
    imageUrl: resolvedImage
  };
};
